/* eslint-disable complexity */

import {Typography} from 'components/Typography';
import {selectProductData} from 'modules/product-card/selectors';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import classNames from 'classnames';
import {DiscountPercentLabel} from 'modules/product-card/components/DiscountPercentLabel';
import {ProductBuy} from 'modules/product-card/components/ProductBuy';
import {ProductShieldSuperPrice} from 'modules/product-card/components/ProductShieldSuperPrice';
import {ProductShieldOnlineEcom} from 'modules/product-card/components/ProductShieldOnlineEcom';
import {useProductBasePrice} from 'hooks/product/useProductBasePrice';
import {useProductIsPriceRed} from 'hooks/product/useProductIsPriceRed';
import {useProductResultPrice} from 'hooks/product/useProductResultPrice';
import {ProductShieldPricePerOne} from 'modules/product-card/components/ProductShieldPricePerOne';
import {ProductReceivingMethod} from 'modules/product-card/components/ProductReceivingMethod';
import style from './style.module.scss';
import {useIsProductFineAndRare} from 'modules/product-card/hooks/useIsProductFineAndRare';
import {useProductReceivingMethods} from 'modules/product-card/hooks/useProductReceivingMethods';
import {useContext} from 'react';
import {ProductPageContext} from 'modules/product-card/context';
import WineryWarning from 'modules/product-card/components/ProductReceivingMethod/ProductReceivingMethodWineryWarning/WineryWarning';
import {CashbackLabel} from 'components/CashbackLabel';
import YandexSplitWidget from 'modules/product-card/components/YandexSplitWidget/YandexSplitWidget';
import {NEXT_PUBLIC_WINTER_DESIGN} from 'core/helpers/env';
import Icon from 'components/Icon';
import {showModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useInView} from 'react-intersection-observer';
import {useLoyaltyRestrictionInformerGtag} from 'modules/product-card/analytics/gtag/hooks/useLoyaltyRestrictionInformerGtag';

interface IProductPrice {
    hideMap?: {
        basePrice?: boolean;
        discountPercent?: boolean;
        productBuy?: boolean;
        receivingMethod?: boolean;
        shields?: boolean;
    };
}

const HIDE_MAP_DEFAULT = {
    basePrice: false,
    discountPercent: false,
    productBuy: false,
    receivingMethod: false,
    shields: false,
};

const HIDE_MAP_FINE_N_RARE = {
    basePrice: true,
    discountPercent: true,
    productBuy: false,
    receivingMethod: false,
    shields: true,
};

export const ProductPriceBlock = ({hideMap: _hideMap = {}}: IProductPrice) => {
    let hideMap = {...HIDE_MAP_DEFAULT, ..._hideMap};

    const {expressWineshop: userWineshop} = useContext(ProductPageContext);
    const productData = useAppSelector(selectProductData);
    const dispatch = useAppDispatch();

    const hasLoyaltyRestrictions = Boolean(productData?.price?.loyaltyRestrictionHint);
    const isPriceRed = useProductIsPriceRed(productData);
    const basePrice = useProductBasePrice(productData);
    const resultPrice = useProductResultPrice(productData);
    const isFineAndRareCategory = useIsProductFineAndRare();

    if (isFineAndRareCategory) {
        hideMap = {...HIDE_MAP_FINE_N_RARE, ..._hideMap};
    }

    const {isUserWineshopTodayWarning} = useProductReceivingMethods();

    const {handleLoyaltyRestrictionIconClick, handleLoyaltyRestrictionIconInView} = useLoyaltyRestrictionInformerGtag({
        product: productData ? {id: productData.id, title: productData.title, price: productData.price} : null,
    });

    const {ref: loyaltyRestrictionIconRef} = useInView({
        triggerOnce: true,
        onChange: (inView) => {
            handleLoyaltyRestrictionIconInView(inView);
        },
    });

    const showLoyaltyRestrictionInfo = () => {
        handleLoyaltyRestrictionIconClick();
        dispatch(showModal(MODALS.PRODUCT_LOYALTY_RESTRICTION_MODAL.name));
    };

    return (
        <>
            {/* NEW_YEAR_2026*/}
            <div
                className={classNames(style.main, {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'pdp-buy-block-winter-design': NEXT_PUBLIC_WINTER_DESIGN,
                })}
            >
                {!hideMap.shields && (
                    <div className={style.shieldsArea}>
                        <ProductShieldPricePerOne />
                        <ProductShieldSuperPrice />
                        <ProductShieldOnlineEcom />
                        {/* <ProductShieldGrowingDiscount />*/}
                    </div>
                )}
                <div className={style.priceArea}>
                    {basePrice && (
                        <Typography className={style.priceBase} variant={'label-16-r'}>
                            {basePrice}
                        </Typography>
                    )}
                    <div className={style.priceMainBlockContainer}>
                        <div className={style.priceMainBlock}>
                            <Typography
                                className={classNames(style.price, {
                                    [style.priceRed]: isPriceRed,
                                })}
                            >
                                {resultPrice}
                            </Typography>
                            <DiscountPercentLabel
                                className={style.desktop}
                                borderRadius={'8px'}
                                variant={'label-16-m'}
                            />
                            <DiscountPercentLabel
                                className={style.tablet}
                                borderRadius={'4px'}
                                height={'20px'}
                                variant={'label-12-r'}
                            />
                        </div>

                        {hasLoyaltyRestrictions && (
                            <div
                                ref={loyaltyRestrictionIconRef}
                                role={'button'}
                                className={style.loyaltyRestriction}
                                onClick={showLoyaltyRestrictionInfo}
                            >
                                <Icon icon={'info-new-light-stroke'} />
                            </div>
                        )}

                        <CashbackLabel value={productData?.price?.cashback} withTooltip={true} withShortForm={false} />
                    </div>
                </div>
                {!hideMap.productBuy && (productData?.canBuy || productData?.canReserve) && (
                    <div className={style.buyButtonArea}>
                        <ProductBuy />
                    </div>
                )}
                {!hideMap.receivingMethod && (productData?.canBuy || productData?.canReserve) && (
                    <ProductReceivingMethod />
                )}
            </div>
            {userWineshop && isUserWineshopTodayWarning && <WineryWarning titleVinoteki={userWineshop.titleVinoteki} />}
            {productData?.yandexSplitInformer && (
                <YandexSplitWidget yandexSplitInformer={productData.yandexSplitInformer} />
            )}
        </>
    );
};
